<script lang="ts">
	import type { InventoryMessages$result } from '$houdini'
	import type { i18n } from 'types/common'

	import { Table, Td } from '@isoftdata/svelte-table'
	import { graphql } from '$houdini'
	import titleCase from 'to-title-case'
	import Icon from '@isoftdata/svelte-icon'
	import { getContext } from 'svelte'

	type Message = InventoryMessages$result['inventory']['messages'][0]

	interface Props {
		inventoryId: number
	}

	let { inventoryId }: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	const messagesQuery = graphql(`
		query InventoryMessages($inventoryId: UInt!) {
			inventory(id: $inventoryId) {
				messages {
					type
					trigger
					text
					display
					id
				}
			}
		}
	`)

	async function loadMessages(inventoryId: number): Promise<Array<Message>> {
		return inventoryId
			? ((await messagesQuery.fetch({ variables: { inventoryId }, policy: 'CacheOrNetwork' }))?.data?.inventory
					?.messages ?? [])
			: []
	}

	const messages = $derived(loadMessages(inventoryId))
</script>

{#await messages}
	<Icon
		icon="spinner"
		class="fa-xl fa-spin"
	/>
{:then loadedMessages}
	<Table
		responsive
		rows={loadedMessages}
		columns={[
			{
				property: 'text',
				name: translate('common:text', 'Text'),
				title: translate('partSearch.theBodyOfTheMessage', 'The body of the message'),
			},
			{
				property: 'trigger',
				name: translate('partSearch.trigger', 'Trigger'),
				title: translate(
					'partSearch.theTypeOfEventThatWillTriggerMessageDelivery',
					'The type of event that will trigger message delivery',
				),
			},
			{
				property: 'type',
				name: translate('partSearch.messageType', 'Message Type'),
				title: translate(
					'partSearch.theDocumentTypeThisMessageIsAssociatedWith',
					'The document type this message is associated with',
				),
			},
			{
				property: 'display',
				name: translate('partSearch.display', 'Display'),
				title: translate('partSearch.howTheMessageAppears', 'How the message appears'),
			},
		]}
	>
		{#snippet children({ row: message })}
			{@const { text, trigger, type, display } = message}
			<tr>
				<Td property="text">{text}</Td>
				<Td property="trigger">{trigger}</Td>
				<Td property="type">{titleCase(type)}</Td>
				<Td property="display">{titleCase(display)}</Td>
			</tr>
		{/snippet}
	</Table>
{:catch error}
	<p>{translate('partSearch.errorLoadingPartMessages', 'Error loading part messages.')}</p>
{/await}
